import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, X, Image, File, Upload, Loader2, Palette } from 'lucide-react';
import { useProduct, useCreateProduct, useUpdateProduct } from '../../hooks/useProducts';
import { useCategories } from '../../hooks/useCategories';
import { useSuppliers } from '../../hooks/useSuppliers';
import { productSchema, ProductFormData } from '../../lib/validation';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatCurrency } from '../../lib/utils';
import api from '../../api/clients';

type ProductFormDataExtended = ProductFormData & {
  porcentajeGanancia?: number | null;
  porcentajeIngresosBrutos?: number | null;
};

export function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnPage = searchParams.get('returnPage') || '1';

  const isEditing = !!id;

  const { data: product, isLoading: productLoading } = useProduct(id ? parseInt(id) : 0);
  const { categories, loading: categoriesLoading } = useCategories();

  const { data: suppliersData } = useSuppliers({ limit: 1000 });
  const suppliers = Array.isArray(suppliersData?.data) ? suppliersData.data : [];

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();

  const [imagenes, setImagenes] = useState<string[]>([]);
  const [documentoPdfUrl, setDocumentoPdfUrl] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormDataExtended>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      nombre: '', descripcion: '', categoriaId: undefined, marca: '', modelo: '', sku: '',
      codigoFabricante: '', codigoBarras: '', proveedorId: undefined, garantia: '', peso: 0,
      dimensiones: '', color: '#000000', precioCosto: 0, precio: 0, stock: 0, stockMinimo: 0,
      ganancia: undefined, ingresoBruto: undefined, costoAdicional: 0, comisiones: 0,
      observaciones: '', activo: true, precioFinal: 0, precioContado: 0,
      porcentajeGanancia: undefined, porcentajeIngresosBrutos: undefined,
    },
  });

  const precioCosto = useWatch({ control, name: 'precioCosto' });
  const ganancia = useWatch({ control, name: 'ganancia' });
  const ingresoBruto = useWatch({ control, name: 'ingresoBruto' });
  const costoAdicional = useWatch({ control, name: 'costoAdicional' });
  const comisiones = useWatch({ control, name: 'comisiones' });
  const stock = useWatch({ control, name: 'stock' });
  const stockMinimo = useWatch({ control, name: 'stockMinimo' });

  const calcularPrecioFinal = () => {
    const costo = Number(precioCosto) || 0;
    const gan = Number(ganancia) || 0;
    const ib = Number(ingresoBruto) || 0;
    const ca = Number(costoAdicional) || 0;
    const com = Number(comisiones) || 0;

    if (costo === 0 && gan === 0 && ib === 0 && ca === 0 && com === 0) return 0;

    const precioConGanancia = costo * (1 + gan / 100);
    const divisorIB = 1 - ib / 100;
    const precioConIB = divisorIB > 0 ? precioConGanancia / divisorIB : precioConGanancia;
    const precioConComision = precioConIB * (1 + com / 100);

    return Math.round((precioConComision + ca) * 100) / 100;
  };

  const precioFinal = calcularPrecioFinal();

  useEffect(() => {
    setValue('precioFinal', precioFinal);
    setValue('precioContado', precioFinal);
    if (!isEditing) setValue('precio', precioFinal);
    if (ganancia !== undefined && ganancia !== null && !isNaN(ganancia)) setValue('porcentajeGanancia' as any, Number(ganancia));
    if (ingresoBruto !== undefined && ingresoBruto !== null && !isNaN(ingresoBruto)) setValue('porcentajeIngresosBrutos' as any, Number(ingresoBruto));
  }, [precioFinal, ganancia, ingresoBruto, isEditing, setValue]);

  useEffect(() => {
    if (product && isEditing) {
      setValue('nombre', product.nombre || '');
      setValue('descripcion', product.descripcion || '');
      setValue('categoriaId', product?.categoriaId ?? undefined);
      setValue('marca', product.marca || '');
      setValue('modelo', product.modelo || '');
      setValue('sku', product.sku || '');
      setValue('codigoFabricante', product.codigoFabricante || '');
      setValue('codigoBarras', product.codigoBarras || '');
      setValue('proveedorId', product.proveedorId || undefined);
      setValue('garantia', product.garantia || '');
      setValue('peso', Number(product.peso || 0));
      setValue('dimensiones', product.dimensiones || '');
      setValue('color', product.color || '#000000');
      setValue('stock', product.stock || 0);
      setValue('stockMinimo', product.stockMinimo || 0);
      setValue('observaciones', product.observaciones || '');
      setValue('activo', product.activo !== false);
      setValue('precioCosto', Number(product.precioCosto || 0));
      setValue('precio', Number(product.precio || 0));
      setValue('precioFinal', Number(product.precioFinal || product.precio || 0));
      setValue('precioContado', Number(product.precioContado || product.precio || 0));
      setValue('ganancia', product.ganancia !== null && product.ganancia !== undefined ? Number(product.ganancia) : product.porcentajeGanancia !== null && product.porcentajeGanancia !== undefined ? Number(product.porcentajeGanancia) : undefined);
      setValue('ingresoBruto', product.ingresoBruto !== null && product.ingresoBruto !== undefined ? Number(product.ingresoBruto) : product.porcentajeIngresosBrutos !== null && product.porcentajeIngresosBrutos !== undefined ? Number(product.porcentajeIngresosBrutos) : undefined);
      setValue('costoAdicional', Number(product.costoAdicional || 0));
      setValue('comisiones', Number(product.comisiones || 0));

      if (product.porcentajeGanancia !== null && product.porcentajeGanancia !== undefined) setValue('porcentajeGanancia' as any, Number(product.porcentajeGanancia));
      else if (product.ganancia !== null && product.ganancia !== undefined) setValue('porcentajeGanancia' as any, Number(product.ganancia));

      if (product.porcentajeIngresosBrutos !== null && product.porcentajeIngresosBrutos !== undefined) setValue('porcentajeIngresosBrutos' as any, Number(product.porcentajeIngresosBrutos));
      else if (product.ingresoBruto !== null && product.ingresoBruto !== undefined) setValue('porcentajeIngresosBrutos' as any, Number(product.ingresoBruto));

      if (product.imagenes) {
        try {
          const urls = typeof product.imagenes === 'string' ? product.imagenes.split(',').filter((u: string) => u.trim()) : [];
          setImagenes(urls);
        } catch {
          setImagenes([]);
        }
      } else if (product.imagenUrl) {
        setImagenes([product.imagenUrl]);
      }

      if (product.documentoPdfUrl) setDocumentoPdfUrl(product.documentoPdfUrl);
    }
  }, [product, isEditing, setValue]);

  const uploadFiles = async (files: File[]): Promise<{ imagenes: string[]; pdfUrl: string | null }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
    });

    return response.data.data;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > 6) {
      toast.error('Máximo 6 archivos (5 imágenes + 1 PDF)');
      return;
    }

    const validFiles = files.filter((file) => {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error(`"${file.name}" no es un formato válido`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" supera el límite de 10MB`);
        return false;
      }
      return true;
    });

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeImagen = (url: string) => {
    setImagenes((prev) => prev.filter((u) => u !== url));
  };

  const removePdf = () => {
    setDocumentoPdfUrl(null);
  };

  const onSubmit = async (data: ProductFormDataExtended) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      let imagenesUrls: string[] = [...imagenes];
      let pdfUrlFinal: string | null = documentoPdfUrl;

      if (selectedFiles.length > 0) {
        const result = await uploadFiles(selectedFiles);
        imagenesUrls = [...imagenesUrls, ...result.imagenes];
        pdfUrlFinal = result.pdfUrl || pdfUrlFinal;
      }

      let status = 'available';
      if (data.stock === 0) status = 'out_of_stock';
      else if (data.stock <= (data.stockMinimo || 0)) status = 'low_stock';

      const payload = {
        ...data,
        precioFinal,
        precioContado: precioFinal,
        porcentajeGanancia: data.ganancia !== undefined && data.ganancia !== null ? Number(data.ganancia) : null,
        porcentajeIngresosBrutos: data.ingresoBruto !== undefined && data.ingresoBruto !== null ? Number(data.ingresoBruto) : null,
        imagenes: imagenesUrls.join(','),
        imagenUrl: imagenesUrls.length > 0 ? imagenesUrls[0] : null,
        documentoPdfUrl: pdfUrlFinal,
        status,
      };

      if (isEditing && id) {
        await updateProduct.mutateAsync({ id: parseInt(id), data: payload });
        toast.success('Producto actualizado exitosamente');
      } else {
        await createProduct.mutateAsync(payload);
        toast.success('Producto creado exitosamente');
      }

      navigate(`/productos?page=${returnPage}`);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar el producto');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (productLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando producto...</div>
      </div>
    );
  }

  const totalFiles = imagenes.length + (documentoPdfUrl ? 1 : 0) + selectedFiles.length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/productos?page=${returnPage}`)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm p-6">
        {/* INFORMACIÓN GENERAL */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Información General</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del producto *</label>
            <Input
              {...register('nombre')}
              placeholder="Ej: Heladera Whirlpool Xpert Inverter"
              error={errors.nombre?.message}
              maxLength={67}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{(watch('nombre') || '').length}/67 caracteres</p>
          </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
              <Input {...register('marca')} placeholder="Ej: Whirlpool" error={errors.marca?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
              <Input {...register('modelo')} placeholder="Ej: Xpert Inverter 320" error={errors.modelo?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
              <select
                value={watch('categoriaId') ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setValue('categoriaId', val ? parseInt(val) : undefined);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Seleccionar categoría...</option>
                {categoriesLoading ? (
                  <option value="" disabled>Cargando categorías...</option>
                ) : (
                  categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))
                )}
              </select>
              {errors.categoriaId && <p className="text-sm text-red-600 mt-1">{errors.categoriaId.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
              <Input {...register('sku')} placeholder="SKU interno del producto" error={errors.sku?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código de barras</label>
              <Input {...register('codigoBarras')} placeholder="Ej: 7891234567890" error={errors.codigoBarras?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código del fabricante</label>
              <Input {...register('codigoFabricante')} placeholder="Código del fabricante" error={errors.codigoFabricante?.message} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea {...register('descripcion')} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Descripción del producto..." />
            </div>
          </div>
        </div>

        {/* COSTOS */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Costos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio de costo *</label>
              <Input type="number" step="0.01" {...register('precioCosto', { valueAsNumber: true })} placeholder="0.00" error={errors.precioCosto?.message} />
              <p className="text-xs text-gray-500 mt-1">Costo base del producto</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ganancia (%)</label>
              <Input type="number" step="0.01" {...register('ganancia', { valueAsNumber: true })} placeholder="Ej: 30" error={errors.ganancia?.message} />
              <p className="text-xs text-gray-500 mt-1">Margen de ganancia sobre el costo</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ingreso Bruto (%)</label>
              <Input type="number" step="0.01" {...register('ingresoBruto', { valueAsNumber: true })} placeholder="Ej: 5" error={errors.ingresoBruto?.message} />
              <p className="text-xs text-gray-500 mt-1">Impuestos o retenciones</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comisión (%)</label>
              <Input type="number" step="0.01" {...register('comisiones', { valueAsNumber: true })} placeholder="Ej: 10" error={errors.comisiones?.message} />
              <p className="text-xs text-gray-500 mt-1">Porcentaje de comisión por venta</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Costos adicionales</label>
              <Input type="number" step="0.01" {...register('costoAdicional', { valueAsNumber: true })} placeholder="0.00" error={errors.costoAdicional?.message} />
              <p className="text-xs text-gray-500 mt-1">Costos extra (envío, packaging, etc.)</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio Final (calculado automáticamente)</label>
              <div className="px-3 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 font-semibold text-xl">
                {formatCurrency(precioFinal)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Este es el precio de venta final. Se calcula automáticamente a partir de: Costo + Ganancia + Ingresos Brutos + (Comisión % sobre el subtotal) + Costos Adicionales</p>
            </div>
          </div>
        </div>

        {/* STOCK Y PROVEEDOR */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Stock y Proveedor</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
              <Input type="number" {...register('stock', { valueAsNumber: true })} placeholder="0" error={errors.stock?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock Mínimo</label>
              <Input type="number" {...register('stockMinimo', { valueAsNumber: true })} placeholder="0" error={errors.stockMinimo?.message} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
              <select {...register('proveedorId', { valueAsNumber: true })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="">Seleccionar proveedor...</option>
                {suppliers.map((sup) => (
                  <option key={sup.id} value={sup.id}>{sup.nombre}</option>
                ))}
              </select>
              {errors.proveedorId && <p className="text-sm text-red-600 mt-1">{errors.proveedorId.message}</p>}
            </div>
          </div>
        </div>

        {/* COMERCIAL */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Comercial</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Garantía</label>
              <Input {...register('garantia')} placeholder="Ej: 1 año, 3 meses, etc." error={errors.garantia?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Peso (kg)</label>
              <Input type="number" step="0.01" {...register('peso', { valueAsNumber: true })} placeholder="0.00" error={errors.peso?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dimensiones</label>
              <Input {...register('dimensiones')} placeholder="Ej: 30x20x10 cm" error={errors.dimensiones?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 items-center gap-2">
                <Palette size={16} className="text-gray-400" /> Color
              </label>
              <div className="flex gap-3">
                <Input {...register('color')} placeholder="Ej: Blanco, Negro, Azul, etc." error={errors.color?.message} className="flex-1" />
                <div className="flex items-center gap-2">
                  <input type="color" value={watch('color') || '#000000'} onChange={(e) => setValue('color', e.target.value)} className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer shrink-0" />
                  {watch('color') && <span className="text-xs text-gray-400 font-mono">{watch('color')}</span>}
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado del producto</label>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">Stock: {stock || 0} unidades</span>
                {stock === 0 ? (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Agotado</span>
                ) : stock <= (stockMinimo || 0) ? (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">Stock bajo</span>
                ) : (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">En stock</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* OBSERVACIONES */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Observaciones Internas</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea {...register('observaciones')} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Observaciones internas del producto..." />
          </div>
        </div>

        {/* ARCHIVOS */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Archivos</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Upload size={16} className="inline mr-1" /> Imágenes y PDF (máximo 5 imágenes + 1 PDF)
            </label>
            <div className="flex items-center gap-3">
              <input type="file" accept="image/*,application/pdf" multiple onChange={handleFileSelect} className="hidden" id="file-upload" disabled={totalFiles >= 6} />
              <label htmlFor="file-upload" className={`px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer flex items-center gap-2 ${totalFiles >= 6 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Upload size={18} className="text-gray-400" />
                <span className="text-sm text-gray-500">{totalFiles >= 6 ? 'Límite alcanzado' : 'Seleccionar archivos'}</span>
              </label>
              <span className="text-xs text-gray-400">{totalFiles}/6 archivos</span>
              {uploading && (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-blue-600" />
                  <span className="text-xs text-blue-600">Subiendo... {uploadProgress}%</span>
                </div>
              )}
            </div>
            {selectedFiles.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Archivos seleccionados:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                      {file.type === 'application/pdf' ? <File size={14} className="text-red-500" /> : <Image size={14} className="text-blue-500" />}
                      <span className="text-xs text-gray-600 truncate max-w-25">{file.name}</span>
                      <button type="button" onClick={() => removeSelectedFile(index)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {imagenes.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-500 mb-1">Imágenes subidas:</p>
                <div className="flex flex-wrap gap-2">
                  {imagenes.map((url, index) => (
                    <div key={index} className="relative group">
                      <img src={url} alt={`Producto ${index + 1}`} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={() => removeImagen(url)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {documentoPdfUrl && (
              <div className="mt-2">
                <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                  <File size={16} className="text-red-500" />
                  <a href={documentoPdfUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-xs">{documentoPdfUrl.split('/').pop() || 'Ver PDF'}</a>
                  <button type="button" onClick={removePdf} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ACTIVO Y FECHAS */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" {...register('activo')} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                <span className="text-sm text-gray-700">Producto activo</span>
              </label>
            </div>
            {isEditing && product && (
              <div className="text-sm text-gray-500 text-right">
                <p>Creado: {new Date(product.createdAt).toLocaleString()}</p>
                <p>Actualizado: {new Date(product.updatedAt).toLocaleString()}</p>
              </div>
            )}
          </div>
        </div>

        {/* CAMPOS OCULTOS */}
        <input type="hidden" {...register('precio')} value={precioFinal} />
        <input type="hidden" {...register('precioFinal')} value={precioFinal} />
        <input type="hidden" {...register('precioContado')} value={precioFinal} />
        <input type="hidden" {...register('porcentajeGanancia' as any)} value={ganancia || 0} />
        <input type="hidden" {...register('porcentajeIngresosBrutos' as any)} value={ingresoBruto || 0} />

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <Button type="submit" disabled={isSubmitting || uploading}>
            <Save size={18} />
            {uploading ? 'Subiendo archivos...' : isSubmitting ? 'Guardando...' : isEditing ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate(`/productos?page=${returnPage}`)} // 👈 Cancelar vuelve a la página correcta
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}